import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiFileText,
  FiDownload,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';

import { useAuth } from '../auth/AuthProviderUnified';
import logger from '../utils/logger.js';
import { API_BASE_URL } from '../config/api.config';

const ComplianceReportsViewer = ({
  entityType = 'company',
  entityId = null,
  defaultInterval = 'weekly',
  showWorkerDetails = false,
}) => {
  const { user } = useAuth();
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState(defaultInterval);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  const intervals = [
    { value: 'daily', label: 'Daily', icon: '📅' },
    { value: 'weekly', label: 'Weekly', icon: '📊' },
    { value: 'monthly', label: 'Monthly', icon: '📋' },
    { value: 'quarterly', label: 'Quarterly', icon: '📈' },
    { value: 'semiannual', label: 'Semi-Annual', icon: '📑' },
    { value: 'yearly', label: 'Yearly', icon: '📜' },
  ];

  // Fetch compliance dashboard summary
  const fetchDashboardSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/compliance-reports/dashboard/summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('vh_auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardSummary(data.summary);
      }
    } catch (error) {
      logger.error('Error fetching dashboard summary', { error: error });
    }
  }, []);

  // Fetch compliance reports
  const fetchComplianceReports = useCallback(
    async (interval) => {
      setLoading(true);
      setError(null);

      try {
        const targetEntityId = entityId || user?.organizationId || user?.organization;
        let endpoint;

        if (entityType === 'worker') {
          endpoint = `/api/compliance-reports/worker/${targetEntityId}?interval=${interval}`;
        } else {
          endpoint = `/api/compliance-reports/company/${targetEntityId}?interval=${interval}&includeWorkerDetails=${showWorkerDetails}`;
        }

        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('vh_auth_token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch compliance reports');
        }

        const data = await response.json();

        setReports((prev) => ({
          ...prev,
          [interval]: data,
        }));

        setLastGenerated(new Date().toISOString());
      } catch (error) {
        logger.error('Error fetching compliance reports', { error, interval });
        setError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [entityType, entityId, user?.organization, showWorkerDetails]
  );

  // Generate on-demand report
  const generateOnDemandReport = useCallback(
    async (interval) => {
      setLoading(true);

      try {
        const targetEntityId = entityId || user?.organizationId || user?.organization;
        const response = await fetch(`${API_BASE_URL}/api/compliance-reports/generate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('vh_auth_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entityType,
            entityId: targetEntityId,
            interval,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to generate report');
        }

        const data = await response.json();

        setReports((prev) => ({
          ...prev,
          [interval]: data,
        }));

        setLastGenerated(new Date().toISOString());
      } catch (error) {
        logger.error('Error generating on-demand report', { error, interval });
        setError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [entityType, entityId, user?.organization]
  );

  // Callback handlers for JSX
  const handleGenerateReportClick = useCallback(() => {
    generateOnDemandReport(selectedInterval);
  }, [generateOnDemandReport, selectedInterval]);

  const createIntervalHandler = useCallback((intervalValue) => {
    return () => setSelectedInterval(intervalValue);
  }, []);

  // Initialize data
  useEffect(() => {
    fetchDashboardSummary();
    fetchComplianceReports(selectedInterval);
  }, [fetchDashboardSummary, fetchComplianceReports, selectedInterval]);

  const currentReport = reports[selectedInterval];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Compliance Reports</h2>
          <p className="text-gray-600">
            {entityType === 'worker' ? 'Individual Worker' : 'Company-Level'} Compliance Analytics
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          {lastGenerated && (
            <div className="text-sm text-gray-500 flex items-center space-x-1">
              <FiClock className="w-4 h-4" />
              <span>Last updated: {new Date(lastGenerated).toLocaleTimeString()}</span>
            </div>
          )}

          <button
            onClick={handleGenerateReportClick}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Dashboard Summary */}
      {dashboardSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Workers</p>
                <p className="text-2xl font-bold text-blue-900">
                  {dashboardSummary.totalDirectWorkers || dashboardSummary.totalWorkers || 0}
                </p>
              </div>
              <FiUsers className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Compliance Rate</p>
                <p className="text-2xl font-bold text-green-900">
                  {dashboardSummary.complianceRate || 0}%
                </p>
              </div>
              <FiCheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Recent Activity</p>
                <p className="text-2xl font-bold text-orange-900">
                  {dashboardSummary.recentActivityCount || 0}
                </p>
              </div>
              <FiFileText className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          {dashboardSummary.scPartners !== undefined && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">SC Partners</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {dashboardSummary.scPartners || 0}
                  </p>
                </div>
                <FiUsers className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          )}

          {dashboardSummary.gcPartners !== undefined && (
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">GC Partners</p>
                  <p className="text-2xl font-bold text-indigo-900">
                    {dashboardSummary.gcPartners || 0}
                  </p>
                </div>
                <FiUsers className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Interval Selection */}
      <div className="flex flex-wrap gap-2">
        {intervals.map((interval) => (
          <button
            key={interval.value}
            onClick={createIntervalHandler(interval.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedInterval === interval.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="mr-2">{interval.icon}</span>
            {interval.label}
          </button>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FiAlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center space-x-3">
            <FiRefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-gray-600">Generating compliance report...</span>
          </div>
        </div>
      )}

      {/* Report Content */}
      {currentReport && !loading && (
        <div className="space-y-6">
          {/* Individual Worker Report */}
          {entityType === 'worker' && currentReport.report && (
            <IndividualWorkerReportDisplay report={currentReport.report} />
          )}

          {/* Company Report */}
          {entityType === 'company' && currentReport.companyReport && (
            <CompanyReportDisplay
              report={currentReport.companyReport}
              workerReports={currentReport.workerReports}
              interval={selectedInterval}
            />
          )}

          {/* Worker Reports List (for company view) */}
          {entityType === 'company' &&
            currentReport.workerReports &&
            currentReport.workerReports.length > 0 && (
              <WorkerReportsTable
                workerReports={currentReport.workerReports}
                interval={selectedInterval}
              />
            )}
        </div>
      )}

      {/* No Data State */}
      {!loading && !currentReport && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Data</h3>
          <p className="text-gray-600 mb-4">
            No compliance data available for the selected {selectedInterval} period.
          </p>
          <button
            onClick={handleGenerateReportClick}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Generate Report
          </button>
        </div>
      )}
    </div>
  );
};

// Individual Worker Report Display Component
const IndividualWorkerReportDisplay = ({ report }) => {
  if (!report) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      {/* Worker Info Header */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Individual Compliance Report - {report.workerInfo.firstName} {report.workerInfo.lastName}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <span>Role: {report.workerInfo.role}</span>
          <span>Company: {report.workerInfo.organization.name}</span>
          <span>
            Period: {new Date(report.reportPeriod.startDate).toLocaleDateString()} -{' '}
            {new Date(report.reportPeriod.endDate).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Overall Score"
          value={`${Math.round(report.complianceMetrics.overallComplianceScore || 0)}%`}
          icon={FiCheckCircle}
          color="blue"
          trend={report.complianceMetrics.improvementTrend}
        />
        <MetricCard
          title="Safety Score"
          value={Math.round(report.safetyPerformance.safetyScore || 0)}
          icon={FiTrendingUp}
          color={
            report.safetyPerformance.riskLevel === 'low'
              ? 'green'
              : report.safetyPerformance.riskLevel === 'medium'
                ? 'yellow'
                : 'red'
          }
        />
        <MetricCard
          title="Total Activities"
          value={report.activitySummary.totalSubmissions || 0}
          icon={FiFileText}
          color="purple"
        />
        <MetricCard
          title="Training Hours"
          value={`${Math.round(report.trainingProgress.totalTrainingHours * 10) / 10}h`}
          icon={FiClock}
          color="indigo"
        />
      </div>

      {/* Activity Summary */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Activity Summary</h4>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">PPE Compliance:</span>
              <span className="ml-2 font-medium">
                {Math.round(report.activitySummary.ppeComplianceRate || 0)}%
              </span>
            </div>
            <div>
              <span className="text-gray-600">Quiz Average:</span>
              <span className="ml-2 font-medium">
                {Math.round(report.activitySummary.averageQuizScore || 0)}%
              </span>
            </div>
            <div>
              <span className="text-gray-600">Hazard Reports:</span>
              <span className="ml-2 font-medium">
                {report.activitySummary.hazardReportsSubmitted || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Jobsites Worked:</span>
              <span className="ml-2 font-medium">{report.activitySummary.jobsitesWorked || 0}</span>
            </div>
            <div>
              <span className="text-gray-600">Avg Daily Activity:</span>
              <span className="ml-2 font-medium">
                {Math.round((report.activitySummary.averageSubmissionsPerDay || 0) * 10) / 10}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {report.recommendations && report.recommendations.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Action Items & Recommendations</h4>
          <div className="space-y-3">
            {report.recommendations.map((rec, _index) => (
              <div
                key={`rec-${rec.priority}-${rec.category}-${rec.title || rec.description}`}
                className={`border-l-4 pl-4 py-3 ${
                  rec.priority === 'critical'
                    ? 'border-red-500 bg-red-50'
                    : rec.priority === 'high'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{rec.recommendation}</p>
                    <p className="text-sm text-gray-600 mt-1">{rec.action}</p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      rec.priority === 'critical'
                        ? 'bg-red-100 text-red-800'
                        : rec.priority === 'high'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {rec.priority}
                  </span>
                </div>
                {rec.timeline && (
                  <p className="text-xs text-gray-500 mt-2">Timeline: {rec.timeline}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Company Report Display Component
const CompanyReportDisplay = ({ report, workerReports, interval }) => {
  if (!report) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        Company Compliance Report -{' '}
        {interval ? interval.charAt(0).toUpperCase() + interval.slice(1) : 'All Time'}
      </h3>

      {/* Company metrics would go here */}
      <div className="text-center py-8 text-gray-500">
        <p>Company-level compliance report display coming soon...</p>
        <p>Worker count: {workerReports?.length || 0}</p>
      </div>
    </div>
  );
};

// Worker Reports Table Component
const WorkerReportsTable = ({ workerReports, interval: _interval }) => {
  if (!workerReports || workerReports.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h4 className="text-lg font-medium text-gray-900">
          Individual Worker Reports ({workerReports.length})
        </h4>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Worker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Overall Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Safety Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activities
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {workerReports.map((report, index) => (
              <tr
                key={`worker-${report.workerInfo?.workerId || report.reportId || index}`}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {report.workerInfo.firstName} {report.workerInfo.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{report.workerInfo.role}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {Math.round(report.complianceMetrics.overallComplianceScore || 0)}%
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {Math.round(report.safetyPerformance.safetyScore || 0)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      report.safetyPerformance.riskLevel === 'low'
                        ? 'bg-green-100 text-green-800'
                        : report.safetyPerformance.riskLevel === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {report.safetyPerformance.riskLevel}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {report.activitySummary.totalSubmissions || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button className="text-blue-600 hover:text-blue-800 mr-4">View Details</button>
                  <button className="text-gray-600 hover:text-gray-800">
                    <FiDownload className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Reusable Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color = 'blue', trend = null }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  return (
    <div className={`p-4 rounded-lg ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium opacity-80">{title}</p>
          <div className="flex items-center space-x-2">
            <p className="text-2xl font-bold">{value}</p>
            {trend && (
              <span className="text-xs">
                {trend === 'improving' ? (
                  <FiTrendingUp className="w-3 h-3" />
                ) : trend === 'declining' ? (
                  <FiTrendingDown className="w-3 h-3" />
                ) : null}
              </span>
            )}
          </div>
        </div>
        <Icon className="w-8 h-8" />
      </div>
    </div>
  );
};

ComplianceReportsViewer.propTypes = {
  entityType: PropTypes.oneOf(['worker', 'company']),
  entityId: PropTypes.string,
  defaultInterval: PropTypes.oneOf([
    'daily',
    'weekly',
    'monthly',
    'quarterly',
    'semiannual',
    'yearly',
  ]),
  showWorkerDetails: PropTypes.bool,
};

IndividualWorkerReportDisplay.propTypes = {
  report: PropTypes.object.isRequired,
};

CompanyReportDisplay.propTypes = {
  report: PropTypes.object,
  workerReports: PropTypes.array,
  interval: PropTypes.string.isRequired,
};

WorkerReportsTable.propTypes = {
  workerReports: PropTypes.array.isRequired,
  interval: PropTypes.string.isRequired,
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.oneOf(['blue', 'green', 'yellow', 'red', 'purple', 'indigo']),
  trend: PropTypes.oneOf(['improving', 'stable', 'declining']),
};

export default ComplianceReportsViewer;
